import React, {
  createContext, useContext, useState, useEffect,
} from 'react';
import Cookies from 'universal-cookie';

const cookies = new Cookies();

const FavoriteButtonsContext = createContext();

export const useFavoriteButtons = () => {
  const context = useContext(FavoriteButtonsContext);
  if (!context) {
    throw new Error('useFavoriteButtons must be used within a FavoriteButtonsProvider');
  }
  return context;
};

export const FavoriteButtonsProvider = ({ children }) => {
  const [favorites, setFavorites] = useState({});
  const [mediaFavorites, setMediaFavorites] = useState({});
  const [residenceFavorites, setResidenceFavorites] = useState({});

  const favoritesCookie = cookies.get('favorites');
  const hasFavoritesCookie = favoritesCookie !== null && favoritesCookie !== undefined;
  useEffect(() => {
    if (favoritesCookie?.favorites) {
      setFavorites(favoritesCookie.favorites);
    }
  }, [hasFavoritesCookie]);

  const mediaFavoritesCookie = cookies.get('media_favorites')
  const hasMediaFavoritesCookie = mediaFavoritesCookie !== null && mediaFavoritesCookie !== undefined;
  useEffect(() => {
    if (mediaFavoritesCookie?.favorites) {
      setMediaFavorites(mediaFavoritesCookie.favorites);
    }
  }, [hasMediaFavoritesCookie]);

  const residenceFavoritesCookie = cookies.get('residence_favorites');
  const hasResidenceFavoritesCookie = residenceFavoritesCookie !== null && residenceFavoritesCookie !== undefined;
  useEffect(() => {
    if (residenceFavoritesCookie?.favorites) {
      setResidenceFavorites(residenceFavoritesCookie.favorites);
    }
  }, [hasResidenceFavoritesCookie]);

  const isFavorited = (type, uuid) => {
    if (type === 'virtual_tour' || type === 'video' || type === 'photo_gallery') {
      return mediaFavorites[type]?.includes(uuid) || false;
    }
    if (type === 'residence') {
      return residenceFavorites[type]?.includes(uuid) || false;
    }
    return favorites[type]?.includes(uuid) || false;
  };

  const toggleFavorite = (type, uuid, floorPlanUuid = null) => {
    let currentFavorites = favorites;
    let setCurrentFavorites = setFavorites;
    let cookieName = 'favorites';
    if (type === 'virtual_tour' || type === 'video' || type === 'photo_gallery') {
      currentFavorites = mediaFavorites;
      setCurrentFavorites = setMediaFavorites;
      cookieName = 'media_favorites';
    } else if (type === 'residence') {
      currentFavorites = residenceFavorites;
      setCurrentFavorites = setResidenceFavorites;
      cookieName = 'residence_favorites';
    }

    const newFavorites = { ...currentFavorites };

    // Get current array or create empty one
    const currentArray = newFavorites[type] || [];
    const index = currentArray.indexOf(uuid);
    const isCurrentlyFavorited = index >= 0;

    if (isCurrentlyFavorited) {
      // Remove from favorites - create new array without the item
      const filteredArray = currentArray.filter((_, i) => i !== index);
      if (filteredArray.length === 0) {
        delete newFavorites[type];
      } else {
        newFavorites[type] = filteredArray;
      }
    } else {
      // Add to favorites - create new array with the item
      newFavorites[type] = [...currentArray, uuid];
    }

    // Update state
    setCurrentFavorites(newFavorites);

    // Update cookie
    const newCookieData = { favorites: newFavorites };
    cookies.remove(cookieName);
    cookies.set(cookieName, newCookieData, {
      path: '/',
      maxAge: 14400, // 4 hour cookie expiration
      domain: window.location.hostname,
    });

    // If this is a residence and we have a floor plan UUID, also toggle the floor plan
    if (type === 'residence' && floorPlanUuid) {
      const floorPlanFavorites = { ...favorites };
      const currentFloorPlanArray = floorPlanFavorites.floor_plan || [];
      const fpIndex = currentFloorPlanArray.indexOf(floorPlanUuid);

      if (isCurrentlyFavorited) {
        // Remove floor plan from favorites (if it exists)
        if (fpIndex >= 0) {
          const filteredArray = currentFloorPlanArray.filter((_, i) => i !== fpIndex);
          if (filteredArray.length === 0) {
            delete floorPlanFavorites.floor_plan;
          } else {
            floorPlanFavorites.floor_plan = filteredArray;
          }
        }
      } else {
        // Add floor plan to favorites (if not already there)
        if (fpIndex < 0) {
          floorPlanFavorites.floor_plan = [...currentFloorPlanArray, floorPlanUuid];
        }
      }

      // Update floor plan favorites state
      setFavorites(floorPlanFavorites);

      // Update floor plan favorites cookie
      const fpCookieData = { favorites: floorPlanFavorites };
      cookies.remove('favorites');
      cookies.set('favorites', fpCookieData, {
        path: '/',
        maxAge: 14400, // 4 hour cookie expiration
        domain: window.location.hostname,
      });
    }

    return !isCurrentlyFavorited; // Return new favorited state
  };

  const value = {
    favorites,
    setFavorites,
    mediaFavorites,
    residenceFavorites,
    setResidenceFavorites,
    isFavorited,
    toggleFavorite,
  };

  return (
    <FavoriteButtonsContext.Provider value={value}>
      {children}
    </FavoriteButtonsContext.Provider>
  );
};

export default FavoriteButtonsContext;